import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const Block = ({ delay, className, children, testId }) => (
  <motion.div
    data-testid={testId}
    initial={{ opacity: 0, y: 14, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay, duration: 0.55, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const CHIPS = [
  { label: "+ CONVERSÃO", className: "-right-4 top-16", delay: 0 },
  { label: "SEO", className: "-left-5 top-40", delay: 1.1 },
  { label: "ADS", className: "-right-3 bottom-16", delay: 0.6 },
];

export const SiteBuilder = ({ startDelay = 2 }) => {
  const reduce = useReducedMotion();
  const d = startDelay;
  return (
    <div className="relative mx-auto w-full max-w-[320px] sm:max-w-[420px] lg:max-w-[460px]" data-testid="site-builder">
      <div aria-hidden="true" className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgba(199,205,214,0.15),transparent_65%)] blur-2xl" />

      <motion.div
        initial={{ opacity: 0, y: 46, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: d, duration: 0.9, ease: EASE }}
        className="card-chrome relative overflow-hidden rounded-2xl shadow-[0_50px_120px_-40px_rgba(199,205,214,0.35)]"
      >
        <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.02] px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-gb-silver/50" />
          <span className="ml-3 flex-1 rounded-full border border-white/10 bg-gb-black/60 px-3 py-1 font-mono text-[10px] tracking-[0.18em] text-gb-silver">
            gobax.com.br
          </span>
          <motion.span
            aria-hidden="true"
            animate={reduce ? {} : { opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="h-1.5 w-1.5 rounded-full bg-gb-silverhi"
          />
        </div>

        <div className="relative space-y-3.5 p-5 sm:p-6">
          <Block delay={d + 0.35} testId="builder-nav" className="flex items-center gap-2.5">
            <img src="/assets/logo.jpg" alt="" width={26} height={26} className="h-6 w-6 rounded-full ring-1 ring-white/20" />
            <span className="h-1.5 w-10 rounded-full bg-white/15" />
            <span className="h-1.5 w-8 rounded-full bg-white/10" />
            <span className="h-1.5 w-8 rounded-full bg-white/10" />
            <span className="btn-chrome ml-auto h-4 w-14 rounded-full" />
          </Block>

          <Block delay={d + 0.55} testId="builder-title" className="space-y-2 pt-2">
            <div className="h-3.5 w-[86%] rounded-md bg-gradient-to-r from-gb-silverhi via-gb-silver to-gb-silver/40" />
            <div className="h-3.5 w-[58%] rounded-md bg-white/15" />
          </Block>

          <Block delay={d + 0.75} testId="builder-visual" className="relative h-24 overflow-hidden rounded-xl border border-white/10 bg-gb-ink sm:h-28">
            <div className="grid-bg absolute inset-0" />
            <img src="/assets/logo.jpg" alt="" width={44} height={44} className="absolute left-4 top-1/2 h-11 w-11 -translate-y-1/2 rounded-full ring-1 ring-white/25" />
            <div className="absolute left-[76px] top-1/2 -translate-y-1/2 space-y-1.5">
              <span className="block h-1.5 w-24 rounded-full bg-white/20" />
              <span className="block h-1.5 w-16 rounded-full bg-white/10" />
            </div>
            <motion.div
              aria-hidden="true"
              animate={reduce ? {} : { y: ["-120%", "1500%"] }}
              transition={{ duration: 3.6, repeat: Infinity, repeatDelay: 1.6, ease: "linear" }}
              className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-transparent via-gb-silverhi/15 to-transparent"
            />
          </Block>

          <Block delay={d + 0.9} testId="builder-text" className="space-y-1.5">
            <div className="h-1.5 w-full rounded-full bg-white/10" />
            <div className="h-1.5 w-[74%] rounded-full bg-white/[0.08]" />
          </Block>

          <Block delay={d + 1.05} testId="builder-cta" className="flex items-center gap-2.5 pt-1">
            <motion.span
              animate={reduce ? {} : { scale: [1, 1.05, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="btn-chrome inline-flex h-7 w-28 items-center justify-center rounded-full font-mono text-[9px] font-semibold tracking-[0.14em]"
            >
              WHATSAPP
            </motion.span>
            <span className="h-7 w-20 rounded-full border border-white/15" />
          </Block>

          <Block delay={d + 1.2} testId="builder-chart" className="rounded-xl border border-white/10 bg-gb-black/50 p-3 pt-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-mono text-[8px] uppercase tracking-[0.22em] text-gb-gray">Conversões</span>
              <span className="font-mono text-[8px] uppercase tracking-[0.14em] text-gb-silver">crescimento</span>
            </div>
            <svg viewBox="0 0 300 60" className="h-12 w-full" aria-hidden="true">
              <defs>
                <linearGradient id="chartStroke" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#5C6570" />
                  <stop offset="100%" stopColor="#EFF3F8" />
                </linearGradient>
              </defs>
              <motion.path
                d="M4 52 C 40 50, 55 44, 80 40 S 120 34, 145 30 S 190 22, 215 18 S 265 8, 296 6"
                fill="none"
                stroke="url(#chartStroke)"
                strokeWidth="2"
                strokeLinecap="round"
                initial={reduce ? false : { pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: d + 1.35, duration: 1.4, ease: "easeInOut" }}
              />
            </svg>
          </Block>
        </div>
      </motion.div>

      {CHIPS.map((chip, i) => (
        <motion.span
          key={chip.label}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: [0, -9, 0] }}
          transition={{
            opacity: { delay: d + 1.5 + i * 0.15, duration: 0.5 },
            scale: { delay: d + 1.5 + i * 0.15, duration: 0.5 },
            y: { duration: 5, repeat: Infinity, delay: chip.delay, ease: "easeInOut" },
          }}
          className={`absolute z-10 rounded-full border border-gb-silver/30 bg-gb-black/85 px-3 py-1.5 font-mono text-[9px] tracking-[0.2em] text-gb-silverhi shadow-[0_8px_30px_-10px_rgba(199,205,214,0.4)] backdrop-blur-sm ${chip.className}`}
        >
          {chip.label}
        </motion.span>
      ))}
    </div>
  );
};
